import { useEffect, useRef, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { LuX } from 'react-icons/lu';
import { useAgente } from '../contexts/AgenteContext';
import BrikAgente from './BrikAgente';
import './AgentePainel.css';

/**
 * Onde o painel do agente NÃO tem porta: a Home e a conversa em tela cheia já
 * são o agente.
 */
export const rotaSemPortaDoAgente = (caminho: string) =>
    caminho === '/home' || caminho === '/' || caminho.startsWith('/assistente');

/* ─── O chamado: a regra de frequência ──────────────────────────────────
   06/10, à noite, pedido dele: "to sentindo falta dos balões aparecendo de vez
   em quando". A regra da marca (uma vez por tela, uma por dia) deixava o
   balão quase invisível, e passou a um a cada 4–6 min.
   07/10, de novo: "faça com que os balões apareçam mais vezes". Agora o
   primeiro vem 8 s depois de chegar numa tela, e no máximo um a cada 1,5–2,5
   min sorteados, em qualquer tela com a porta, com a frase girando entre as
   da tela. Fechado no × três vezes no dia, pausa até amanhã. Continua só no
   navegador, com o id do usuário na chave; sem armazenamento, não aparece. */
const INTERVALO_MIN = 90 * 1000;
const INTERVALO_SORTEIO = 60 * 1000;
const PRIMEIRA_ESPERA = 8 * 1000;
/* Ocupado (digitando, com diálogo aberto ou com a aba escondida): tenta de novo em 15 s. */
const NOVA_TENTATIVA = 15 * 1000;
/* A regra mudou em 07/10: o registro de antes, e os × de hoje, não valem mais. */
const VERSAO = 2;

interface RegistroChamado { versao: number; ultimo: number; dia: string; fechadosHoje: number; giro: Record<string, number>; }

const chaveChamado = () => {
    try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        return u?.id ? `lemeia:brik-chamado:${u.id}` : null;
    } catch { return null; }
};

const lerChamado = (chave: string): RegistroChamado | null => {
    try {
        const v = JSON.parse(localStorage.getItem(chave) || 'null');
        /* Registro de outra versão da regra (ou do formato antigo, dia + telas)
           recomeça a contagem; o giro das frases continua de onde estava. */
        const atual = v?.versao === VERSAO;
        return {
            versao: VERSAO,
            ultimo: atual && typeof v?.ultimo === 'number' ? v.ultimo : 0,
            dia: atual && typeof v?.dia === 'string' ? v.dia : '',
            fechadosHoje: atual && typeof v?.fechadosHoje === 'number' ? v.fechadosHoje : 0,
            giro: v?.giro && typeof v.giro === 'object' ? v.giro : {},
        };
    } catch { return null; }
};

const gravarChamado = (chave: string, r: RegistroChamado) => {
    try { localStorage.setItem(chave, JSON.stringify(r)); } catch { /* sem armazenamento */ }
};

const hoje = () => new Date().toISOString().slice(0, 10);

/** A tela, sem os ids: "/pipeline/deal/171" e "/pipeline/deal/9" são a mesma tela. */
const telaDoCaminho = (caminho: string) => caminho.split('/').filter(p => p && !/^\d+$/.test(p)).join('/') || 'home';

/* Um balão por vez no app inteiro: a gaveta de Tarefas monta um segundo
   cabeçalho por cima do primeiro. */
let chamadoNaTela = false;

/* As frases giram por tela. Só pedidos que o Brik AI já sabe atender —
   nada de lote, mensagem ao cliente ou excluir. */
const FRASES: Record<string, string[]> = {
    'pipeline/deal': ['“anota e me lembra sexta”', '“resume essa oportunidade”', '“move para negociação”', '“marca uma reunião amanhã às 15h”', '“ganhei essa”'],
    pipeline: ['“o que parou de andar?”', '“quanto tem em negociação?”', '“quem está abaixo do ritmo?”'],
    tarefas: ['“o que vence hoje?”', '“o que eu tenho para hoje?”', '“adia a tarefa de amanhã para sexta”'],
    agenda: ['“o que tenho amanhã?”', '“marca uma reunião sexta às 10h”'],
    contacts: ['“procura o contato da Maria”', '“o que eu tenho para hoje?”'],
    monitoramento: ['“como estamos na meta deste mês?”', '“quem está abaixo do ritmo?”'],
};
const FRASES_GERAIS = ['“o que eu tenho para hoje?”', '“como estamos na meta deste mês?”', '“o que parou de andar?”'];

const fraseDaTela = (tela: string, giro: number) => {
    const lista = FRASES[tela] ?? FRASES_GERAIS;
    return lista[giro % lista.length];
};

/**
 * A porta do agente: o Brik, o último item do cabeçalho de cada tela (direção
 * B, 06/10). Fica junto das ações da tela porque é isso que ele é — a ação
 * desta tela dita em palavras.
 *
 * Marca em `docs/propostas/marca-do-agente.html`: o Brik é o próprio botão,
 * sem borda e sem fundo. Desde 07/10/2026 (pedido dele) o tijolo tem a altura
 * dos botões vizinhos, 32px, e não mais 15px dentro de uma caixa de 24.
 */
const AgentePorta: FC<{
    /** `celular`: o botão do cabeçalho do MobileNav, ao lado do sino, com o Brik de 26px. */
    variante?: 'cabecalho' | 'celular';
}> = ({ variante = 'cabecalho' }) => {
    const agente = useAgente();
    const { pathname } = useLocation();
    const refBotao = useRef<HTMLButtonElement>(null);
    const [balao, setBalao] = useState<{ top: number; right: number; frase: string } | null>(null);

    const semPorta = !agente || rotaSemPortaDoAgente(pathname);
    const aberto = !!agente?.aberto;
    const tela = telaDoCaminho(pathname);

    /* O chamado de vez em quando. Só no cabeçalho do desktop — no celular o
       balão cobriria o conteúdo inteiro. Agenda o próximo para quando o
       intervalo desde o último vencer; na hora, se a pessoa estiver digitando,
       com diálogo aberto ou com a aba escondida, tenta de novo em 30 s. */
    useEffect(() => {
        if (semPorta || variante !== 'cabecalho' || aberto) return;
        const chave = chaveChamado();
        if (!chave) return;

        let vivo = true;
        let t: ReturnType<typeof setTimeout>;

        const agendar = (espera: number) => { t = setTimeout(tentar, espera); };

        const tentar = () => {
            if (!vivo) return;
            const r = lerChamado(chave);
            if (!r) return;
            const fechadosHoje = r.dia === hoje() ? r.fechadosHoje : 0;
            if (fechadosHoje >= 3) return;

            const falta = r.ultimo + INTERVALO_MIN - Date.now();
            if (falta > 0) { agendar(falta + Math.random() * INTERVALO_SORTEIO); return; }

            const ativo = document.activeElement as HTMLElement | null;
            const digitando = !!ativo && (/^(INPUT|TEXTAREA|SELECT)$/.test(ativo.tagName) || ativo.isContentEditable);
            const modal = !!document.querySelector('[role="dialog"], .od-veu, .rp');
            const b = refBotao.current?.getBoundingClientRect();
            if (chamadoNaTela || digitando || modal || document.hidden || !b || b.width === 0) { agendar(NOVA_TENTATIVA); return; }

            const giro = r.giro[tela] ?? 0;
            chamadoNaTela = true;
            gravarChamado(chave, { ...r, ultimo: Date.now(), dia: hoje(), fechadosHoje, giro: { ...r.giro, [tela]: giro + 1 } });
            setBalao({ top: b.bottom + 10, right: window.innerWidth - b.right, frase: fraseDaTela(tela, giro) });
            agendar(INTERVALO_MIN + Math.random() * INTERVALO_SORTEIO);
        };

        agendar(PRIMEIRA_ESPERA);
        return () => { vivo = false; clearTimeout(t); };
    }, [semPorta, variante, aberto, tela]);

    /* O balão dura 8 s, some com Esc, com clique fora e ao abrir o painel. */
    useEffect(() => {
        if (!balao) return;
        const fechar = () => { setBalao(null); chamadoNaTela = false; };
        const t = setTimeout(fechar, 8000);
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') fechar(); };
        const aoClicar = (e: MouseEvent) => {
            if (!(e.target as HTMLElement).closest?.('.mrc-balao')) fechar();
        };
        window.addEventListener('keydown', aoTeclar);
        window.addEventListener('mousedown', aoClicar);
        return () => {
            clearTimeout(t);
            window.removeEventListener('keydown', aoTeclar);
            window.removeEventListener('mousedown', aoClicar);
        };
    }, [balao]);

    useEffect(() => { if (aberto && balao) { setBalao(null); chamadoNaTela = false; } }, [aberto, balao]);

    if (semPorta || !agente) return null;

    const fecharNoX = () => {
        const chave = chaveChamado();
        const r = chave ? lerChamado(chave) : null;
        if (chave && r) gravarChamado(chave, { ...r, dia: hoje(), fechadosHoje: (r.dia === hoje() ? r.fechadosHoje : 0) + 1 });
        setBalao(null);
        chamadoNaTela = false;
    };

    const classe = variante === 'celular'
        ? 'mobile-header-btn agt-porta-celular'
        : `agt-porta ${aberto ? 'is-aberto' : ''}`;

    return (
        <>
            <button
                ref={refBotao}
                type="button"
                className={classe}
                onClick={agente.alternar}
                aria-label="Pergunte ao Brik AI"
                aria-expanded={aberto}
                title="Pergunte ao Brik AI"
            >
                <BrikAgente
                    px={variante === 'celular' ? 26 : 32}
                    justo={variante !== 'celular'}
                    estado={balao ? 'chamado' : 'repouso'}
                    animar={!aberto}
                />
            </button>

            {balao && createPortal(
                <div
                    className="mrc-balao"
                    role="status"
                    style={{ top: balao.top, right: balao.right }}
                    onClick={() => { setBalao(null); chamadoNaTela = false; agente.abrir(); }}
                >
                    <span>Peça ao <b>Brik AI</b>: {balao.frase}</span>
                    <button
                        type="button"
                        className="mrc-balao-x"
                        aria-label="Fechar"
                        onClick={e => { e.stopPropagation(); fecharNoX(); }}
                    >
                        <LuX size={14} />
                    </button>
                </div>,
                document.body,
            )}
        </>
    );
};

export default AgentePorta;
